import type { UsageSheetAppData } from '@shared/types/forge-usage-sheet';
import type { DataTableCell, DataTableColumn } from '@shared/types/views';
import type { AppCollection, AppRecord } from '@/lib/app-records';

/**
 * U1 (02/11/2026): the app as it will be, filled with the Forge test's
 * objects, before it exists. Each row of the test's table becomes one object of
 * the list, with the first status, as a run merges into a new list
 * (`merge_run_into_app_f5a`, mode « rows »). Nothing is written: the objects
 * live in the page.
 */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const PREVIEW_RECORD_PREFIX = 'preview:';

export const previewRecordId = (rowId: string) => `${PREVIEW_RECORD_PREFIX}${rowId}`;

export const projectTestApp = (
  appData: UsageSheetAppData | undefined,
  table: Record<string, unknown> | null | undefined,
  now: Date = new Date(),
): { collection: AppCollection; records: AppRecord[] } | null => {
  if (!appData || appData.mode !== 'rows' || !table || !Array.isArray(table.columns) || !Array.isArray(table.rows)) return null;
  const columns = table.columns.filter(isRecord)
    .filter((column) => typeof column.key === 'string' && typeof column.label === 'string') as unknown as DataTableColumn[];
  if (columns.length === 0) return null;
  const statuses = appData.statuses.map((status) => ({ key: status.key, label: status.label, tone: 'neutral' as const, ...(status.done ? { done: true } : {}) }));
  const firstStatus = statuses[0]?.key ?? 'todo';
  const at = now.toISOString();
  const records = table.rows.filter(isRecord).filter((row) => typeof row.id === 'string').map((row): AppRecord => {
    const cells = (Array.isArray(row.cells) ? row.cells : []).filter(isRecord)
      .filter((cell) => typeof cell.key === 'string') as unknown as DataTableCell[];
    return {
      id: previewRecordId(String(row.id)), recordKey: String(row.id), computed: cells, computedByDomino: cells, edits: {},
      status: firstStatus, statusChangedBy: null, statusChangedAt: null, assigneeUserId: null, dueDate: null, archivedAt: null,
      firstRunId: null, lastRunId: null, firstSeenAt: at, lastSeenAt: at, version: 0, ownerUserId: null, aiGrid: null,
    };
  });
  return {
    collection: {
      id: 'preview', title: appData.title, mode: 'rows', columns, statuses, reviewGrid: null, lastMerge: null, objectKind: appData.object ?? null,
    },
    records,
  };
};
